import { AccessGrant, Location, VisitorGroup } from 'graphql/generated/graphqlTypes';

interface IdOnly {
  id: string;
}

/**
 * A visitor group whose members and access grant locations (device groups) and doors (devices) only hold an `id`.
 * The locations also hold the ids of their doors.
 */
export type VisitorGroupWithIds = Omit<VisitorGroup, 'members' | 'accessGrants'> & {
  members: IdOnly[];
  accessGrants: (Omit<AccessGrant, 'locations' | 'doors'> & {
    locations: (IdOnly & { doors: IdOnly[] })[];
    doors: IdOnly[];
  })[];
};

/**
 * The slim groups query only selects the ids of the members and of the access grant locations (device groups) and
 * doors (devices). Turn them back into the `members`, `locations` and `doors` the helpers read, with only an `id`
 * each. The locations also carry the ids of their doors, taken from the slim locations query.
 */
export const rebuildVisitorGroupsFromIds = (
  visitorGroups: VisitorGroup[],
  locations: Location[]
): VisitorGroupWithIds[] => {
  const doorsByLocationId = new Map(locations.map((location) => [location.id, location.doors]));

  return visitorGroups.map((visitorGroup) => ({
    ...visitorGroup,
    members: visitorGroup.memberIds.map((memberId) => ({ id: memberId })),
    accessGrants: visitorGroup.accessGrants.map((accessGrant) => ({
      ...accessGrant,
      locations: accessGrant.locationIds.map((locationId) => ({
        id: locationId,
        doors: doorsByLocationId.get(locationId) ?? []
      })),
      doors: accessGrant.doorIds.map((doorId) => ({ id: doorId }))
    }))
  }));
};
